import React, { useState, useEffect } from 'react';
import { ApiURLs } from '../../constant/ApiUrls';
import { fetchData } from '../../services/api';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

/*
  Ad views by month, private sellers against dealers.

  Read from the ad_views log, which holds one row per view with a timestamp -
  not from place_ads.view_count, which is a running total and cannot be broken
  down by month at all.

  The "unknown" line is views of adverts whose account has since been deleted.
  It is drawn rather than folded into private, because folding it in would
  quietly overstate private demand, and on the Isle of Man in December that
  would have been 221 views out of 1,663.
*/

const SITE_OPTIONS = [
    { key: 'self', label: 'This site' },
    { key: 'peer', label: 'Other site' },
    { key: 'both', label: 'Both together' },
];

const RANGE_OPTIONS = [
    { months: 6, label: '6 months' },
    { months: 12, label: '12 months' },
    { months: 24, label: '24 months' },
];

const prettyMonth = (ym) => {
    if (!ym || ym.length < 7) return ym || '';
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = parseInt(ym.slice(5, 7), 10);
    return `${names[m - 1] || ym.slice(5, 7)} ${ym.slice(2, 4)}`;
};

const Card = ({ label, value, colour }) => (
    <div style={{
        background: '#fff', borderRadius: 10, padding: '16px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0',
        minWidth: 160, flex: '1 1 180px',
    }}>
        <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: colour || '#1a1a2e' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
    </div>
);

const ViewsTrend = () => {
    const [site, setSite] = useState('self');
    const [months, setMonths] = useState(12);
    const [rows, setRows] = useState([]);
    const [siteLabel, setSiteLabel] = useState('');
    const [notice, setNotice] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setNotice('');
            try {
                const res = await fetchData(`${ApiURLs.VIEWS_TREND}?site=${site}&months=${months}`);
                if (cancelled) return;
                if (res && res.status === 1 && Array.isArray(res.months)) {
                    setRows(res.months);
                    setSiteLabel(res.site || '');
                    // A partial answer must say so. Drawing our own numbers under
                    // the heading "Both together" would be a quiet lie.
                    if (res.partial) setNotice(`Could not reach the other site (${res.message || 'no reason given'}), so this shows one site only.`);
                } else {
                    setRows([]);
                    setSiteLabel('');
                    setNotice((res && res.message) ? `Could not load: ${res.message}` : 'Could not load the view history.');
                }
            } catch (e) {
                if (!cancelled) { setRows([]); setNotice('Could not load the view history.'); }
            }
            if (!cancelled) setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [site, months]);

    const data = rows.map((r) => ({ ...r, name: prettyMonth(r.ym) }));
    const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    const totalUnknown = sum('unknown');

    // "This month so far" is not comparable with a finished month, so the
    // headline figures deliberately quote the last COMPLETE month.
    const lastComplete = rows.length > 1 ? rows[rows.length - 2] : null;

    return (
        <div style={{ padding: 24, background: '#f7f8fa', minHeight: '100vh' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>Ad Views by Month</h2>
                {siteLabel ? <span style={{ fontSize: 14, color: '#666' }}>{siteLabel}</span> : null}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {SITE_OPTIONS.map((o) => (
                        <button key={o.key} onClick={() => setSite(o.key)}
                            style={{
                                padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                                border: site === o.key ? '1px solid #0088FE' : '1px solid #d5d9e0',
                                background: site === o.key ? '#0088FE' : '#fff',
                                color: site === o.key ? '#fff' : '#333',
                            }}>{o.label}</button>
                    ))}
                    <span style={{ width: 12 }} />
                    {RANGE_OPTIONS.map((o) => (
                        <button key={o.months} onClick={() => setMonths(o.months)}
                            style={{
                                padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                                border: months === o.months ? '1px solid #00C49F' : '1px solid #d5d9e0',
                                background: months === o.months ? '#00C49F' : '#fff',
                                color: months === o.months ? '#fff' : '#333',
                            }}>{o.label}</button>
                    ))}
                </div>
            </div>

            {notice ? (
                <div style={{
                    background: '#fef3c7', border: '1px solid #f6d174', color: '#8a6100',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14,
                }}>{notice}</div>
            ) : null}

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
                <Card label={`Total views (${months} months)`} value={sum('total')} />
                <Card label="Private sellers" value={sum('private')} colour="#0088FE" />
                <Card label="Dealers" value={sum('dealer')} colour="#00C49F" />
                {lastComplete ? (
                    <Card label={`Last full month (${prettyMonth(lastComplete.ym)})`} value={lastComplete.total} />
                ) : null}
            </div>

            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Loading…</div>
                ) : data.length === 0 ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>No views recorded in this period.</div>
                ) : (
                    <ResponsiveContainer width="100%" height={380}>
                        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                            <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                            <Legend />
                            <Line type="monotone" dataKey="private" name="Private sellers" stroke="#0088FE" strokeWidth={2.5} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="dealer" name="Dealers" stroke="#00C49F" strokeWidth={2.5} dot={{ r: 3 }} />
                            {totalUnknown > 0 ? (
                                <Line type="monotone" dataKey="unknown" name="Deleted accounts" stroke="#bbb" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                            ) : null}
                        </LineChart>
                    </ResponsiveContainer>
                )}
                <div style={{ fontSize: 12, color: '#888', marginTop: 12, lineHeight: 1.6 }}>
                    One row per ad view, counted on the month it happened. The current month is still running, so its
                    point will always look low until the month ends.
                    {totalUnknown > 0 ? ' The dashed line is views of adverts whose account has since been deleted - they are shown separately rather than counted as private.' : ''}
                </div>
            </div>
        </div>
    );
};

export default ViewsTrend;
